import { useState, useRef, useEffect } from 'react'
import { useCrosswordStore } from '../../store'
import { findFragmentFitPositions } from '../../lib/fragments'
import { PencilIcon, TrashIcon } from '../Icons'

/**
 * Sidebar tab body listing saved fragments. Each row shows the fragment's
 * placement count, dimensions, and how many positions on the current grid it
 * fits — clicking Apply drops it at the first fit. Was previously a toolbar
 * dropdown; now lives as a flat tab in ExploreColumn.
 */
export function FragmentsPanel() {
  const {
    fragments,
    constructorGrid,
    deleteFragment,
    renameFragment,
    applyFragmentToGrid,
  } = useCrosswordStore()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editingId])

  // Fit-position compute scans every grid cell × every fragment placement —
  // expensive on a populated grid. Defer to a post-paint effect so the tab
  // opens instantly; fit counts fill in one tick later. Recompute only when
  // the grid or fragment list actually changes.
  const [fitPositions, setFitPositions] = useState<Map<string, Array<{ row: number; col: number }>>>(new Map())
  useEffect(() => {
    if (!constructorGrid || fragments.length === 0) {
      setFitPositions(new Map())
      return
    }
    let cancelled = false
    const id = setTimeout(() => {
      if (cancelled) return
      const t0 = performance.now()
      const next = new Map<string, Array<{ row: number; col: number }>>()
      for (const fragment of fragments) {
        next.set(fragment.id, findFragmentFitPositions(constructorGrid, fragment))
      }
      const dt = performance.now() - t0
      console.log(`[perf] fragment fit-positions: ${dt.toFixed(1)}ms (${fragments.length} fragments)`)
      if (!cancelled) setFitPositions(next)
    }, 0)
    return () => { cancelled = true; clearTimeout(id) }
  }, [constructorGrid, fragments])

  let totalFits = 0
  for (const list of fitPositions.values()) totalFits += list.length

  const handleApply = (fragmentId: string) => {
    const positions = fitPositions.get(fragmentId)
    if (!positions || positions.length === 0) return
    const first = positions[0]
    if (!first) return
    applyFragmentToGrid(fragmentId, first.row, first.col)
  }

  const handleStartEdit = (id: string, name: string) => {
    setEditingId(id)
    setEditingName(name)
  }

  const handleFinishEdit = () => {
    if (editingId && editingName.trim()) {
      renameFragment(editingId, editingName.trim())
    }
    setEditingId(null)
    setEditingName('')
  }

  const handleEditKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleFinishEdit()
    else if (e.key === 'Escape') {
      setEditingId(null)
      setEditingName('')
    }
  }

  return (
    <div className="flex flex-col h-full -mx-3 -mb-3">
      <div className="px-3 py-2 bg-gray-50/80 border-b border-gray-100 shrink-0 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700">
          Saved fragments
        </span>
        <span className="text-[10px] text-gray-400">
          {totalFits} fit{totalFits === 1 ? '' : 's'} on grid
        </span>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {fragments.length === 0 ? (
          <div className="px-3 py-6 text-xs text-gray-400">
            Select multiple placements on the grid, then click "Save as fragment"
            to capture them as a reusable group.
          </div>
        ) : (
          <div className="py-1">
            {fragments.map(fragment => {
              const positions = fitPositions.get(fragment.id) ?? []
              const fitCount = positions.length
              const canApply = fitCount > 0
              const isEditing = editingId === fragment.id
              return (
                <div
                  key={fragment.id}
                  className="group flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50"
                >
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <input
                        ref={inputRef}
                        type="text"
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onBlur={handleFinishEdit}
                        onKeyDown={handleEditKey}
                        className="w-full px-1 py-0.5 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (
                      <>
                        <div className="text-xs font-medium text-gray-900 truncate">
                          {fragment.name}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          {fragment.placements.length} placement
                          {fragment.placements.length === 1 ? '' : 's'}
                          {' · '}
                          {fragment.width}×{fragment.height}
                          {' · '}
                          <span className={canApply ? 'text-emerald-600 font-medium' : 'text-gray-400'}>
                            {fitCount} fit{fitCount === 1 ? '' : 's'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => handleApply(fragment.id)}
                    disabled={!canApply}
                    className="px-2 py-0.5 text-[11px] bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
                    title={canApply ? `Apply at (${positions[0]?.row}, ${positions[0]?.col})` : 'No fitting position on the current grid'}
                  >
                    Apply
                  </button>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleStartEdit(fragment.id, fragment.name)}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      title="Rename"
                    >
                      <PencilIcon className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => deleteFragment(fragment.id)}
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <TrashIcon className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
